import { Controller } from "@hotwired/stimulus"

const THEME_CLASSES = ["ai-bar--light", "ai-bar--green", "ai-bar--blue"]

export default class extends Controller {
  static targets = ["expanded", "collapsed"]

  connect() {
    this.isExpanded = true
    this._lastScrollY = window.scrollY
    this._currentTheme = null
    this._boundHandleScroll = this._handleScroll.bind(this)
    window.addEventListener("scroll", this._boundHandleScroll, { passive: true })

    this._setupThemeObserver()
    this._pulseOnFirstVisit()
  }

  disconnect() {
    window.removeEventListener("scroll", this._boundHandleScroll)
    this._themeObserver?.disconnect()
  }

  expand() {
    this.isExpanded = true
    this.expandedTarget.classList.remove("hidden")
    this.collapsedTarget.classList.add("hidden")
  }

  collapse() {
    this.isExpanded = false
    this.expandedTarget.classList.add("hidden")
    this.collapsedTarget.classList.remove("hidden")
  }

  _handleScroll() {
    if (document.activeElement?.closest("[data-controller~=ai-bottom-bar]")) return

    const currentScrollY = window.scrollY
    const scrollingDown = currentScrollY > this._lastScrollY && currentScrollY > 100

    if (scrollingDown && this.isExpanded) {
      this.collapse()
    } else if (!scrollingDown && !this.isExpanded) {
      this.expand()
    }

    this._lastScrollY = currentScrollY
  }

  _setupThemeObserver() {
    this._visibleSections = new Map()

    const sections = document.querySelectorAll("[data-ai-bar-theme]")
    if (!sections.length) return

    this._themeObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this._visibleSections.set(entry.target, entry.target.dataset.aiBarTheme)
          } else {
            this._visibleSections.delete(entry.target)
          }
        }
        this._applyTheme()
      },
      { threshold: 0 }
    )

    for (const section of sections) {
      this._themeObserver.observe(section)
    }
  }

  _pulseOnFirstVisit() {
    const key = "ai-bar-pulse-seen"
    if (localStorage.getItem(key)) return

    localStorage.setItem(key, "1")

    setTimeout(() => {
      this.expandedTarget.classList.add("ai-bar-pulse")
      this.expandedTarget.addEventListener(
        "animationend",
        () => this.expandedTarget.classList.remove("ai-bar-pulse"),
        { once: true }
      )
    }, 1500)
  }

  _applyTheme() {
    // Pick the theme of the lowest visible section (furthest down the page)
    let lastTheme = "green" // default
    let lastTop = -Infinity

    for (const [el, theme] of this._visibleSections) {
      const top = el.getBoundingClientRect().top
      if (top > lastTop) {
        lastTop = top
        lastTheme = theme
      }
    }

    if (lastTheme === this._currentTheme) return

    this._currentTheme = lastTheme
    const className = `ai-bar--${lastTheme}`
    for (const cls of THEME_CLASSES) {
      this.element.classList.toggle(cls, cls === className)
    }
  }
}
